import { addCompanyRepresentative, editCompanyRepresentative } from '@/api/backend/profile';
import { ProfilePermissionType, ProfileRepresentativeResponseType } from '@/api/backend/types';
import { PROFILE_PERMISSION } from '@/lib/constants';
import { Validators } from '@/lib/validator';
import { queryClient } from '@/services/queryClient';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Checkbox,
  FormError,
  FormField,
  FormLabel,
  FormLabelGroup,
  Input,
  useToast,
} from '@opaz/ui-components';
import { useMutation } from '@tanstack/react-query';
import { SubmitHandler, useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

type CompanyRepresentativeFormInput = {
  fullName: string;
  civilNumber: string;
  email: string;
  company?: string;
  permissions: string[];
};

const formSchema = z.object({
  fullName: z.string().min(1).max(100),
  civilNumber: z
    .string()
    .min(1)
    .refine(...Validators.stringIsNumber),
  email: z.string().min(1).email(),
  company: z.string().max(100).optional(),
  permissions: z.array(z.string()).min(1),
});

type CompanyRepresentativeFormProps = {
  representative?: ProfileRepresentativeResponseType;
  onClose: () => void;
};

export const CompanyRepresentativeForm = ({ representative, onClose }: CompanyRepresentativeFormProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const { mutateAsync: addRepresentative, isPending: isAddRepresentativeLoading } = useMutation({
    mutationFn: async (data: CompanyRepresentativeFormInput) => {
      await addCompanyRepresentative({
        fullName: data.fullName,
        civilNumber: data.civilNumber,
        email: data.email,
        company: data.company ?? '',
        permissions: (data.permissions ?? []) as ProfilePermissionType[],
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['profileRepresentatives'],
      });
      toast({
        message: t('profile.company.representativeForm.saveSuccessMessage'),
        variant: 'success',
      });
      onClose();
    },
  });

  const { mutateAsync: editRepresentative, isPending: isEditRepresentativeLoading } = useMutation({
    mutationFn: async (data: CompanyRepresentativeFormInput) => {
      if (!representative) return;

      await editCompanyRepresentative(representative.id.toString(), {
        fullName: data.fullName,
        civilNumber: data.civilNumber,
        email: data.email,
        company: data.company ?? '',
        permissions: (data.permissions ?? []) as ProfilePermissionType[],
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['userProfileRepresentative'],
      });

      toast({
        message: t('profile.company.representativeForm.editSuccessMessage'),
        variant: 'success',
      });

      onClose();
    },
    onError: () => {},
  });

  const {
    control,
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<CompanyRepresentativeFormInput>({
    defaultValues: {
      fullName: representative?.fullName || '',
      civilNumber: representative?.civilNumber || '',
      email: representative?.email || '',
      company: representative?.company || '',
      permissions: representative?.permissions || [],
    },
    resolver: zodResolver(formSchema),
  });

  const { field: permissionsField } = useController({
    control,
    name: 'permissions',
  });

  const onSubmit: SubmitHandler<CompanyRepresentativeFormInput> = async (data) => {
    // Ensure only permission codes are sent
    const permissionCodes = (data.permissions ?? []).map((p) =>
      typeof p === 'string' && PROFILE_PERMISSION[p as ProfilePermissionType]
        ? PROFILE_PERMISSION[p as ProfilePermissionType]
        : p,
    );

    const payload = {
      ...data,
      permissions: permissionCodes,
    };

    if (representative) {
      await editRepresentative(payload);
    } else {
      await addRepresentative(payload);
    }
  };

  return (
    <div>
      <h1 className="text-foreground text-3xl font-bold mb-4">
        {representative
          ? t('profile.company.representativeForm.editTitle')
          : t('profile.company.representativeForm.addTitle')}
      </h1>

      {!representative && <p>{t('profile.company.representativeForm.description')}</p>}

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-5 mt-4 w-full md:w-[632px]">
          {/* Full Name */}
          <FormField>
            <FormLabelGroup>
              <FormLabel error={!!errors.fullName}>{t('profile.company.representativeForm.fields.fullName')}</FormLabel>
              {errors.fullName && (
                <FormError>{t(errors.fullName.message as unknown as TemplateStringsArray)}</FormError>
              )}
            </FormLabelGroup>
            <Input error={!!errors.fullName} {...register('fullName')}></Input>
          </FormField>

          {/* Civil ID */}
          <FormField>
            <FormLabelGroup>
              <FormLabel error={!!errors.civilNumber}>
                {t('profile.company.representativeForm.fields.civilId')}
              </FormLabel>
              {errors.civilNumber && (
                <FormError>{t(errors.civilNumber.message as unknown as TemplateStringsArray)}</FormError>
              )}
            </FormLabelGroup>
            <Input error={!!errors.civilNumber} {...register('civilNumber')}></Input>
          </FormField>

          {/* Email */}
          <FormField>
            <FormLabelGroup>
              <FormLabel error={!!errors.email}>{t('profile.company.representativeForm.fields.email')}</FormLabel>
              {errors.email && <FormError>{t(errors.email.message as unknown as TemplateStringsArray)}</FormError>}
            </FormLabelGroup>
            <Input error={!!errors.email} {...register('email')}></Input>
          </FormField>

          {/* Company (Optional) */}
          <FormField>
            <FormLabelGroup>
              <FormLabel error={!!errors.company}>{t('profile.company.representativeForm.fields.company')}</FormLabel>
              {errors.company && <FormError>{t(errors.company.message as unknown as TemplateStringsArray)}</FormError>}
            </FormLabelGroup>
            <Input error={!!errors.company} {...register('company')}></Input>
          </FormField>
        </div>

        {/* Permissions */}
        <FormField className="mt-5">
          <FormLabelGroup>
            <FormLabel error={!!errors.permissions}>
              {t('profile.company.representativeForm.fields.accountPermission')}
            </FormLabel>

            {errors.permissions && (
              <FormError>{t(errors.permissions.message as unknown as TemplateStringsArray)}</FormError>
            )}
          </FormLabelGroup>

          {[
            PROFILE_PERMISSION.PMS_CONTRACT,
            PROFILE_PERMISSION.PMS_PERMIT,
            PROFILE_PERMISSION.PMS_PAY,
            PROFILE_PERMISSION.PMS_READ,
          ].map((permission) => (
            <Checkbox
              key={permission}
              id={`permission-${permission}`}
              disabled={isAddRepresentativeLoading || isEditRepresentativeLoading}
              checked={permissionsField.value?.includes(permission)}
              onCheckedChange={(checkedState) => {
                const isChecked = Boolean(checkedState);
                let updatedPermissions = permissionsField.value || [];
                if (isChecked && !updatedPermissions.includes(permission)) {
                  updatedPermissions = [...updatedPermissions, permission];
                } else if (!isChecked && updatedPermissions.includes(permission)) {
                  updatedPermissions = updatedPermissions.filter((perm) => perm !== permission);
                }
                permissionsField.onChange(updatedPermissions);
              }}
              size="small"
            >
              {t(
                `profile.company.representativeForm.fields.permissions.${permission}` as unknown as TemplateStringsArray,
              )}
            </Checkbox>
          ))}
        </FormField>

        <div className="flex gap-3 mt-12">
          <Button type="submit" disabled={isAddRepresentativeLoading || isEditRepresentativeLoading}>
            {t('common.save')}
          </Button>

          <Button
            disabled={isAddRepresentativeLoading || isEditRepresentativeLoading}
            variant="secondary"
            onClick={onClose}
            type="button"
          >
            {t('common.cancel')}
          </Button>
        </div>
      </form>
    </div>
  );
};
